import { useCallback, useEffect, useRef, useState } from 'react';

import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useLatest } from 'react-use';
import { Apollo } from 'src/gql/generated';

import { seedSavedSkillAttachments } from './skill-attachment-cache';
import type { SkillAttachmentChanges } from './skill-attachment-editor';
import {
  getHasSkillAttachmentChanges,
  getRemainingSkillAttachmentChanges,
} from './skill-autosave-changes';
import {
  getSkillContentAfterSave,
  getSkillContentError,
  getSkillContentIsEqual,
  getSkillContentWithFinalName,
} from './skill-editor-content';
import { getEditorSkill, getSkillEditorKey } from './skill-editor-draft';
import { getErrorMessage, SKILLS_LIST_REFETCH } from './skill-editor-errors';
import { getAttachmentInput } from './skill-editor-status';
import { getSectionAfterSave } from './skill-file-rules';
import { getIsOutdatedSkillVersionError } from './skill-save-error';
import { createSkillSaveQueue } from './skill-save-queue';
import type { SkillDraftSession, SkillDraftStore } from './use-skill-draft-session';

/** One save sent to the server, kept so a retry after a lost response resends the same request. */
export interface SaveAttempt {
  input: Apollo.AutoSaveAgentSkillDraftInput;
  previousPaths: string[];
  submittedChanges: SkillAttachmentChanges;
  submittedContent: string;
}

type SavePlan =
  | { kind: 'blocked' }
  | { kind: 'invalid'; message: string }
  | { kind: 'unchanged' }
  | { attempt: SaveAttempt; kind: 'send' };

const AUTOSAVE_DELAY_MS = 500;
// Errors that mean the request itself is wrong, so a retry must build a new one.
const FINAL_SAVE_ERROR_CODES = [
  'BAD_USER_INPUT',
  'UNAUTHORIZED',
  'UNAUTHENTICATED',
  'OUTDATED_SKILL_VERSION',
];

/**
 * Saving the draft: one save at a time through a queue, the save when the editor is hidden
 * or unmounted, and Cmd+S.
 */
export function useSkillSaveQueue({ skillId, store }: { skillId: string; store: SkillDraftStore }) {
  // False while the editor is hidden or unmounted: edits reported then are saved right away.
  const activeRef = useRef(false);
  const [autoSaveDraft, { loading: saving }] = Apollo.useAutoSaveAgentSkillDraftMutation({
    errorPolicy: 'none',
  });

  /** One save of whatever is pending. Resolves to false when it failed. */
  async function saveOnce(): Promise<boolean> {
    store.flushEditors();
    const plan = getSavePlan({ skillId, store });
    if (plan.kind === 'invalid') store.reportSaveError({ message: plan.message, outdated: false });
    if (plan.kind !== 'send') return plan.kind === 'unchanged';

    const { attempt } = plan;
    store.saveAttemptRef.current = attempt;
    if (store.saveErrorRef.current) store.writeSaveError(null);

    try {
      const result = await autoSaveDraft({
        refetchQueries: SKILLS_LIST_REFETCH,
        update: (cache, { data }) => {
          const updated = data?.autoSaveAgentSkillDraft.agentSkill;
          if (!updated) return;

          // Set before the cache update renders, so the editor does not reload its own save.
          store.initializedKeyRef.current = getSkillEditorKey(updated);
          seedSavedSkillAttachments({
            cache,
            skill: getEditorSkill(updated),
            submitted: attempt.submittedChanges,
          });
        },
        variables: { input: attempt.input },
      });
      const saved = result.data?.autoSaveAgentSkillDraft.agentSkill;
      if (!saved) throw new Error('The server did not return the updated skill.');

      store.saveAttemptRef.current = null;
      applySavedSkill({ attempt, skill: getEditorSkill(saved), store });
      return true;
    } catch (error: unknown) {
      // A GraphQL field resolver can fail after commit; keep the receipt key for retries.
      if (getIsFinalSaveError(error)) store.saveAttemptRef.current = null;
      store.reportSaveError({
        message: getErrorMessage(error, 'Could not save the skill.'),
        outdated: getIsOutdatedSkillVersionError(error),
      });
      return false;
    }
  }

  // The queue outlives renders, so it calls whichever saveOnce rendered last.
  const saveOnceRef = useLatest(saveOnce);
  const [saveQueue] = useState(() =>
    createSkillSaveQueue(() => saveOnceRef.current(), store.getHasUnsavedChanges)
  );
  const { autosave: requestAutosave, request: requestSave } = saveQueue;

  const handleEditorKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if ((!event.metaKey && !event.ctrlKey) || event.key.toLowerCase() !== 's') return;
      event.preventDefault();
      void requestSave();
    },
    [requestSave]
  );

  /**
   * Save what is pending when the editor is hidden or unmounted. A save already in flight
   * queues this one behind it, and edits the editors flush while unmounting join it too.
   */
  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
      void requestSave();
    };
  }, [requestSave]);

  return { activeRef, handleEditorKeyDown, requestAutosave, requestSave, saveQueue, saving };
}

/**
 * Save once typing pauses, or right away when an action waits on the save. `paused` holds it
 * while something else is in progress: a name being typed, a scope change, the delete dialog.
 */
export function useSkillAutosaveTimer({
  paused,
  session,
}: {
  paused: boolean;
  session: SkillDraftSession;
}) {
  const {
    canEdit,
    contentError,
    dirty,
    editRevision,
    lastEditAtRef,
    pendingAction,
    requestAutosave,
    requestSave,
    saveError,
    saving,
  } = session;

  useEffect(() => {
    if (!canEdit || !dirty || saving || saveError || contentError || paused) return;
    const timer = window.setTimeout(
      () => void (pendingAction ? requestSave() : requestAutosave()),
      pendingAction ? 0 : Math.max(0, lastEditAtRef.current + AUTOSAVE_DELAY_MS - Date.now())
    );
    return () => window.clearTimeout(timer);
  }, [
    canEdit,
    contentError,
    dirty,
    editRevision,
    lastEditAtRef,
    paused,
    pendingAction,
    requestAutosave,
    requestSave,
    saveError,
    saving,
  ]);
}

/**
 * What a save would send. A failed attempt is resent as it was, so a save whose response was
 * lost is not applied twice.
 */
function getSavePlan({ skillId, store }: { skillId: string; store: SkillDraftStore }): SavePlan {
  const expectedRevision = store.skillRef.current?.draft?.revision;
  const lastSavedContent = store.savedContentRef.current;
  if (
    !store.canEditRef.current ||
    expectedRevision === undefined ||
    lastSavedContent === null ||
    store.deletingRef.current ||
    store.saveErrorRef.current?.outdated
  )
    return { kind: 'blocked' };

  const retry = store.saveAttemptRef.current;
  const submittedContent = retry?.submittedContent ?? store.contentRef.current;
  const submittedChanges = retry?.submittedChanges ?? store.attachmentChangesRef.current;
  const previousPaths = retry?.previousPaths ?? store.savedPathsRef.current;
  const content = getSkillContentWithFinalName(submittedContent);
  const message = getSkillContentError(content);
  if (message) return { kind: 'invalid', message };
  if (
    getSkillContentIsEqual(content, lastSavedContent) &&
    !getHasSkillAttachmentChanges(submittedChanges, previousPaths)
  )
    return { kind: 'unchanged' };

  const attempt = retry ?? {
    input: { ...getAttachmentInput(submittedChanges), content, expectedRevision, id: skillId },
    previousPaths,
    submittedChanges,
    submittedContent,
  };
  return { attempt, kind: 'send' };
}

/** Take in the saved skill, keeping whatever the author changed while the save was in flight. */
function applySavedSkill({
  attempt: { previousPaths, submittedChanges, submittedContent },
  skill,
  store,
}: {
  attempt: SaveAttempt;
  skill: Apollo.AgentSkillSimpleFragment;
  store: SkillDraftStore;
}) {
  store.writeSkill(skill);
  store.initializedKeyRef.current = getSkillEditorKey(skill);
  store.savedPathsRef.current = skill.attachments.map(file => file.path);
  const remaining = getRemainingSkillAttachmentChanges({
    current: store.attachmentChangesRef.current,
    previousPaths,
    savedAttachments: skill.attachments,
    submitted: submittedChanges,
  });
  store.writeAttachmentChanges(remaining);
  store.setSection(current =>
    getSectionAfterSave({
      attachments: skill.attachments,
      current,
      remaining,
      submitted: submittedChanges,
    })
  );
  store.writeContent(
    getSkillContentAfterSave({
      current: store.contentRef.current,
      saved: skill.content,
      submitted: submittedContent,
    })
  );
  store.writeSavedContent(skill.content);
}

function getIsFinalSaveError(error: unknown) {
  return (
    CombinedGraphQLErrors.is(error) &&
    error.errors.every(graphqlError =>
      FINAL_SAVE_ERROR_CODES.includes(String(graphqlError.extensions?.code))
    )
  );
}
